import React, { FC, useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { Button, ButtonVariant } from 'components/common/buttons/Button';
import { CentsDataGrid } from 'components/common/dataGrid/dataGrid';
import { IndividualPayrocTerminalAccount } from 'api/admin/stores/editPayrocTerminalAccount';
import { PayrocTerminalAccountInfo } from 'api/admin/stores/getPayrocTerminalAccounts';
import { EditTerminalAccountModal } from './EditTerminalAccountModal';
import styles from './TerminalAccounts.module.scss';
import { usePayrocTerminals } from './queries/usePayrocTerminals';

const COLUMNS = [
  { headerName: 'ID', field: 'id', flex: 1 },
  { headerName: 'Merchant Id', field: 'merchantId', flex: 2 },
  { headerName: 'Terminal Id', field: 'terminalId', flex: 5 },
];

export const TerminalAccounts: FC<{
  storeId: number;
  businessId: number;
}> = ({ storeId, businessId }) => {
  const { isLoading, data: terminalAccounts, refetch } = usePayrocTerminals(storeId);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [terminal, setTerminal] = useState<IndividualPayrocTerminalAccount | null>(null);
  const isAddDisabled = useMemo(() => !isEmpty(terminalAccounts), [terminalAccounts]);

  const handleCreateTerminalAccountClick = () => {
    setTerminal(null);
    setShowEditModal(true);
  };

  const handleRowClick = ({ row }: { row: PayrocTerminalAccountInfo }) => {
    const { id, payrocMerchantAccountId, terminalId } = row;

    setTerminal({ id, payrocMerchantAccountId, terminalId, storeId });
    setShowEditModal(true);
  };

  const handleEditTerminalClose = async (shouldReload: boolean) => {
    if (shouldReload) {
      await refetch();
    }

    setShowEditModal(false);
  };

  return (
    <div className="page-container">
      <div className={styles.header}>
        <Button
          type={ButtonVariant.CONTAINED}
          onClick={handleCreateTerminalAccountClick}
          isDisabled={isAddDisabled}
        >
          Add Terminal Account
        </Button>
      </div>
      <CentsDataGrid
        tableName="merchantAccounts"
        data={terminalAccounts}
        columns={COLUMNS}
        onRowClick={handleRowClick}
        isLoading={isLoading}
      />
      {showEditModal && (
        <EditTerminalAccountModal
          storeId={storeId}
          businessId={businessId}
          isNew={!terminal}
          terminal={terminal}
          onClose={handleEditTerminalClose}
        />
      )}
    </div>
  );
};
